<template>
  <v-container
    fluid
    class="classroom-page"
    :class="{ embedded: ClassId }"
  >
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      :editClassRoom="selectedClassRoom"
      @close="editDialog = false"
      @submit="getList"
    />

    <AssignStudent
      :editDialog="assignStudentDialog"
      :selectedClassRoom="selectedClassRoom"
      @close="assignStudentClose"
      @submit="assigntStudentSubmit"
    />

    <AssignCurriculum
      :editDialog="assignCurriculumDialog"
      :selectedClassRoom="selectedClassRoom"
      @close="assignCurriculumClose"
      @submit="assigntCurriculumSubmit"
    />

    <AssignTeacher
      :editDialog="assignTeacherDialog"
      :selectedClassRoom="selectedClassRoom"
      @close="assignClose"
      @submit="getList"
    />

    <div v-if="!ClassId" class="classroom-page-heading">
      <div>
        <div class="page-eyebrow">Academics</div>
        <h1>Classrooms</h1>
        <p>Manage classrooms, students, teachers, curriculums, and learning materials.</p>
      </div>

      <div class="heading-actions">
        <v-btn outlined color="primary" @click="termTemplatesDialog = true">
          <v-icon left>mdi-file-document-multiple-outline</v-icon>
          Material templates
        </v-btn>

        <v-btn
          v-if="checkRightStatus(29)"
          color="primary"
          depressed
          @click="add"
        >
          <v-icon left>mdi-plus</v-icon>
          New classroom
        </v-btn>
      </div>
    </div>

    <v-row v-if="!ClassId" class="classroom-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-door-open</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ items.length }}</div>
            <div class="metric-label">Total classrooms</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ totalStudents }}</div>
            <div class="metric-label">Total students</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-account-tie-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ assignedTeacherCount }}</div>
            <div class="metric-label">Teacher assignments</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-book-open-page-variant-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ curriculumAssignedCount }}</div>
            <div class="metric-label">Curriculums assigned</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" class="classrooms-card" elevation="0">
      <div v-if="ClassId" class="embedded-header">
        <div>
          <div class="embedded-title">Classrooms</div>
          <div class="embedded-subtitle">
            Classrooms assigned to this department
          </div>
        </div>

        <div class="embedded-actions">
          <v-chip x-small outlined color="primary">{{ items.length }}</v-chip>

          <v-btn
            v-if="checkRightStatus(29)"
            small
            color="primary"
            depressed
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add classroom
          </v-btn>
        </div>
      </div>

      <div class="classroom-filters">
        <v-text-field
          v-model="search"
          class="classroom-search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search classrooms, departments, or teachers"
          single-line
          hide-details
          clearable
        />

        <div class="filter-actions">
          <ClassesDropbox
            v-if="!ClassId"
            class="classroom-department-filter"
            @classSelected="classSelected"
          />

          <v-btn
            icon
            outlined
            color="primary"
            class="classroom-action-btn"
            :loading="loading"
            aria-label="Refresh classrooms"
            @click="getList"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            v-if="ClassId"
            icon
            outlined
            color="primary"
            class="classroom-action-btn"
            aria-label="Material templates"
            @click="termTemplatesDialog = true"
          >
            <v-icon size="19">mdi-file-document-multiple-outline</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        :headers="headers"
        :items="items"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :expanded.sync="expanded"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        item-key="id"
        show-expand
        class="classrooms-table"
      >
        <template v-slot:item="{ item, isExpanded, expand }">
          <tr class="classroom-row">
            <td class="expand-column">
              <v-btn icon small @click.stop="expand(!isExpanded)">
                <v-icon>
                  {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-btn>
            </td>

            <td>
              <div class="classroom-identity">
                <div class="classroom-avatar">
                  <v-icon color="primary" size="20">mdi-door-open</v-icon>
                </div>

                <div class="classroom-name-wrap">
                  <div class="classroom-name">{{ item.name }}</div>
                  <div class="classroom-subtitle">
                    {{ item.studentCount || 0 }}
                    {{ Number(item.studentCount || 0) === 1 ? "student" : "students" }}
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div v-if="item.Class" class="detail-line">
                <v-icon color="primary" size="16">mdi-school-outline</v-icon>
                <div>
                  <strong>{{ item.Class.name }}</strong>
                  <span>Department</span>
                </div>
              </div>

              <div v-else class="detail-line muted-detail">
                <v-icon size="16">mdi-school-outline</v-icon>
                <div>
                  <strong>Not assigned</strong>
                  <span>No department</span>
                </div>
              </div>
            </td>

            <td class="text-center">
              <span class="student-count">{{ item.studentCount || 0 }}</span>
            </td>

            <td>
              <div
                v-if="item.ClassRoomTeachers && item.ClassRoomTeachers.length"
                class="teacher-list"
              >
                <div
                  v-for="teacher in item.ClassRoomTeachers"
                  :key="teacher.id"
                  class="teacher-row"
                >
                  <template v-if="teacher && teacher.Teacher">
                    <v-icon color="primary" size="15">mdi-account-tie-outline</v-icon>
                    <span>{{ teacher.Teacher.name }}</span>
                  </template>
                </div>
              </div>

              <span v-else class="status-label muted-status">
                <v-icon size="14">mdi-account-off-outline</v-icon>
                No teacher
              </span>
            </td>

            <td>
              <div
                v-if="item.Curriculums && item.Curriculums.length"
                class="chip-list"
              >
                <v-chip
                  v-for="curriculum in item.Curriculums"
                  :key="curriculum.id"
                  x-small
                  outlined
                  color="primary"
                >
                  {{ curriculum.name }}
                </v-chip>
              </div>

              <span v-else class="status-label warning-status">
                <v-icon color="error" size="14">mdi-book-off-outline</v-icon>
                No curriculum
              </span>
            </td>

            <td>
              <div
                v-if="item.TermMaterialAssignments && item.TermMaterialAssignments.length"
                class="chip-list"
              >
                <v-chip
                  v-for="assignment in item.TermMaterialAssignments"
                  :key="assignment.id"
                  x-small
                  outlined
                  color="primary"
                >
                  {{
                    assignment.TermMaterialTemplate &&
                    assignment.TermMaterialTemplate.name
                      ? assignment.TermMaterialTemplate.name
                      : "Template"
                  }}
                  <span v-if="assignment.Term && assignment.Term.name" class="ml-1">
                    ({{ assignment.Term.name }})
                  </span>
                </v-chip>
              </div>

              <span v-else class="status-label muted-status">
                <v-icon size="14">mdi-file-outline</v-icon>
                Not assigned
              </span>
            </td>

            <td class="text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Classroom actions"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>

                <v-list dense class="menu-button">
                  <v-list-item @click="createClassRoomReportCard(item)">
                    <v-list-item-icon>
                      <v-icon size="19">mdi-file-chart-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Create Report Card</v-list-item-title>
                  </v-list-item>

                  <v-divider />

                  <v-list-item @click="assignStudentOpen(item)">
                    <v-list-item-icon>
                      <v-icon size="19">mdi-account-plus-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Assign Student</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="assignTeacherOpen(item)">
                    <v-list-item-icon>
                      <v-icon size="19">mdi-account-tie-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Assign Teacher</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="assignCurriculumOpen(item)">
                    <v-list-item-icon>
                      <v-icon size="19">mdi-book-open-page-variant-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Manage Curriculums</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="openAssignTermTemplate(item)">
                    <v-list-item-icon>
                      <v-icon size="19">mdi-file-document-edit-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Assign Materials Covered</v-list-item-title>
                  </v-list-item>

                  <v-divider />

                  <v-list-item
                    v-if="checkRightStatus(31)"
                    @click="editClass(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(32)"
                    @click="deleteClass(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="expanded-column pa-0">
            <div class="classroom-expanded-content">
              <div class="expanded-heading">
                <div>
                  <div class="expanded-icon">
                    <v-icon color="primary" size="19">mdi-account-group-outline</v-icon>
                  </div>

                  <div>
                    <strong>Students in {{ item.name }}</strong>
                    <span>Review and manage students assigned to this classroom.</span>
                  </div>
                </div>

                <v-chip small outlined color="primary">
                  {{ item.studentCount || 0 }}
                  {{ Number(item.studentCount || 0) === 1 ? "student" : "students" }}
                </v-chip>
              </div>

              <div class="embedded-student-list">
                <ClassRoomStudentList
                  :ClassRoomId="item.id"
                  @getList="getList"
                />
              </div>
            </div>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-door-open</v-icon>
            <strong>No classrooms found</strong>
            <span>Add your first classroom to assign teachers, students, and curriculums.</span>

            <v-btn
              v-if="checkRightStatus(29)"
              color="primary"
              depressed
              small
              @click="add"
            >
              <v-icon left small>mdi-plus</v-icon>
              Add classroom
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-magnify-close</v-icon>
            <strong>No matching classrooms</strong>
            <span>No classrooms matched "{{ search }}".</span>

            <v-btn text color="primary" small @click="search = null">
              Clear search
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        :items="items"
        :search="search"
        :loading="loading"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25] }"
        class="mobile-classroom-list"
      >
        <template v-slot:default="{ items: mobileItems }">
          <div
            v-for="item in mobileItems"
            :key="item.id"
            class="mobile-classroom-card"
          >
            <div class="mobile-card-top">
              <div class="classroom-avatar">
                <v-icon color="primary" size="20">mdi-door-open</v-icon>
              </div>

              <div class="mobile-card-identity">
                <strong>{{ item.name }}</strong>
                <span>{{ item.Class ? item.Class.name : "No department" }}</span>
              </div>

              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon>
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="createClassRoomReportCard(item)">
                    <v-list-item-title>Create Report Card</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="assignStudentOpen(item)">
                    <v-list-item-title>Assign Student</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="assignTeacherOpen(item)">
                    <v-list-item-title>Assign Teacher</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="assignCurriculumOpen(item)">
                    <v-list-item-title>Manage Curriculums</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openAssignTermTemplate(item)">
                    <v-list-item-title>Assign Materials Covered</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item
                    v-if="checkRightStatus(31)"
                    @click="editClass(item)"
                  >
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="checkRightStatus(32)"
                    @click="deleteClass(item)"
                  >
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="mobile-classroom-details">
              <div>
                <span>Students</span>
                <strong class="mobile-student-count">{{ item.studentCount || 0 }}</strong>
              </div>

              <div>
                <span>Teachers</span>
                <strong>{{ teacherNames(item) || "Not assigned" }}</strong>
              </div>

              <div>
                <span>Curriculum</span>
                <strong>{{ curriculumNames(item) || "Not assigned" }}</strong>
              </div>

              <div>
                <span>Materials</span>
                <strong>{{ materialCount(item) }}</strong>
              </div>
            </div>

            <v-btn
              text
              small
              color="primary"
              class="mobile-student-toggle"
              @click="toggleMobileStudents(item.id)"
            >
              <v-icon left small>
                {{
                  mobileExpandedId === item.id
                    ? "mdi-chevron-up"
                    : "mdi-account-group-outline"
                }}
              </v-icon>
              {{
                mobileExpandedId === item.id
                  ? "Hide students"
                  : "View students"
              }}
            </v-btn>

            <div
              v-if="mobileExpandedId === item.id"
              class="mobile-expanded-students"
            >
              <ClassRoomStudentList
                :ClassRoomId="item.id"
                @getList="getList"
              />
            </div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-door-open</v-icon>
            <strong>No classrooms found</strong>
            <span>Add your first classroom to assign teachers, students, and curriculums.</span>
          </div>
        </template>
      </v-data-iterator>
    </v-card>

    <TermMaterialTemplates
      :dialog="termTemplatesDialog"
      @close="termTemplatesDialog = false"
    />

    <AssignTermMaterialTemplate
      :dialog="assignTermTemplateDialog"
      :selectedClassRoom="selectedClassRoom"
      @close="closeAssignTermTemplate"
      @saved="getList"
    />

    <ClassroomReportCard
      v-if="classroomReportDialog"
      :ClassRoom="classroomForReport"
      @close="closeClassroomReport"
    />
  </v-container>
</template>

<script>
import classRoomService from "@/modules/ClassRoom/service.js";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

const AssignStudent = () =>
  import("@/modules/ClassRoom/components/AssignStudent.vue");

const AssignCurriculum = () =>
  import("@/modules/ClassRoom/components/AssignCurriculum.vue");

const AssignTeacher = () =>
  import("@/modules/ClassRoom/components/AssignTeacher.vue");

const ClassroomReportCard = () =>
  import("@/modules/ClassRoom/components/ClassroomReportCard.vue");

const ClassRoomStudentList = () =>
  import("@/modules/Student/components/ClassRoomStudentList");

const ClassesDropbox = () =>
  import("@/modules/Classes/components/Dropbox.vue");

const TermMaterialTemplates = () =>
  import("@/modules/TermMaterials/components/TermMaterialTemplates.vue");

const AssignTermMaterialTemplate = () =>
  import("@/modules/TermMaterials/components/AssignTermMaterialTemplate.vue");

export default {
  name: "classroom-list",

  props: {
    ClassId: {
      default: null,
    },
  },

  components: {
    Create,
    Edit,
    AssignStudent,
    AssignCurriculum,
    AssignTeacher,
    ClassroomReportCard,
    ClassRoomStudentList,
    ClassesDropbox,
    TermMaterialTemplates,
    AssignTermMaterialTemplate,
  },

  data() {
    return {
      classroomReportDialog: false,
      classroomForReport: null,
      expanded: [],
      mobileExpandedId: null,
      termTemplatesDialog: false,
      assignTermTemplateDialog: false,
      selectedClassRoom: null,
      createDialog: false,
      assignStudentDialog: false,
      assignCurriculumDialog: false,
      assignTeacherDialog: false,
      selectedTeacher: null,
      selectedStudent: null,
      editDialog: false,

      headers: [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
          width: "60px",
        },
        {
          text: "Classroom",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Department",
          align: "left",
          value: "Class.name",
          sortable: true,
          width: "180px",
        },
        {
          text: "Students",
          align: "center",
          value: "studentCount",
          sortable: true,
          width: "110px",
        },
        {
          text: "Teachers",
          align: "left",
          value: "teacherCount",
          sortable: false,
          width: "190px",
        },
        {
          text: "Curriculum",
          align: "left",
          value: "Curriculums",
          sortable: false,
          width: "230px",
        },
        {
          text: "Materials covered",
          align: "left",
          value: "TermMaterialAssignments",
          sortable: false,
          width: "250px",
        },
        {
          text: "",
          value: "action",
          sortable: false,
          width: "60px",
        },
      ],

      items: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: ["name"],
      itemsPerPage: 10,
      selectedClasses: [],
    };
  },

  created() {
    this.getList();
    document.title = "taleem - Classroom List";
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
    ]),

    totalStudents() {
      return this.items.reduce((total, classroom) => {
        return total + Number(classroom.studentCount || 0);
      }, 0);
    },

    assignedTeacherCount() {
      return this.items.reduce((total, classroom) => {
        const teachers = Array.isArray(classroom.ClassRoomTeachers)
          ? classroom.ClassRoomTeachers.filter(
              (row) => row && row.Teacher
            )
          : [];

        return total + teachers.length;
      }, 0);
    },

    curriculumAssignedCount() {
      return this.items.reduce((total, classroom) => {
        const curriculums = Array.isArray(classroom.Curriculums)
          ? classroom.Curriculums.length
          : 0;

        return total + curriculums;
      }, 0);
    },
  },

  methods: {
    teacherNames(item) {
      if (!Array.isArray(item.ClassRoomTeachers)) return "";

      return item.ClassRoomTeachers
        .filter((row) => row && row.Teacher)
        .map((row) => row.Teacher.name)
        .join(", ");
    },

    curriculumNames(item) {
      if (!Array.isArray(item.Curriculums)) return "";

      return item.Curriculums
        .map((curriculum) => curriculum.name)
        .filter(Boolean)
        .join(", ");
    },

    materialCount(item) {
      const count = Array.isArray(item.TermMaterialAssignments)
        ? item.TermMaterialAssignments.length
        : 0;

      return count
        ? `${count} ${count === 1 ? "assignment" : "assignments"}`
        : "Not assigned";
    },

    toggleMobileStudents(id) {
      this.mobileExpandedId =
        this.mobileExpandedId === id ? null : id;
    },

    openAssignTermTemplate(item) {
      this.selectedClassRoom = item;
      this.assignTermTemplateDialog = true;
    },

    closeAssignTermTemplate() {
      this.assignTermTemplateDialog = false;
      this.selectedClassRoom = null;
    },

    assignTeacherOpen(item) {
      this.selectedClassRoom = item;
      this.assignTeacherDialog = true;
    },

    assigntStudentSubmit() {
      this.assignStudentDialog = false;
      this.selectedClassRoom = null;
      this.getList();
    },

    assigntCurriculumSubmit() {
      this.assignCurriculumDialog = false;
      this.selectedClassRoom = null;
      this.getList();
    },

    assignCurriculumOpen(item) {
      this.selectedClassRoom = item;
      this.assignCurriculumDialog = true;
    },

    assignCurriculumClose() {
      this.selectedClassRoom = null;
      this.assignCurriculumDialog = false;
    },

    assignStudentOpen(item) {
      this.selectedClassRoom = item;
      this.assignStudentDialog = true;
    },

    assignStudentClose() {
      this.selectedStudent = null;
      this.selectedClassRoom = null;
      this.assignStudentDialog = false;
    },

    assignClose() {
      this.assignTeacherDialog = false;
      this.selectedClassRoom = null;
      this.getList();
    },

    add() {
      this.createDialog = true;
    },

    classSelected(value) {
      this.selectedClasses = value;
      this.getList();
    },

    createClassRoomReportCard(classRoom) {
      const curriculumCount = Array.isArray(classRoom.Curriculums)
        ? classRoom.Curriculums.length
        : 0;

      if (!curriculumCount) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "No curriculum assigned to this classroom",
          });

        return;
      }

      if (!Number(classRoom.studentCount || 0)) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "No students found in this classroom",
          });

        return;
      }

      this.classroomForReport = classRoom;
      this.classroomReportDialog = true;
    },

    closeClassroomReport() {
      this.classroomReportDialog = false;
      this.classroomForReport = null;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      const where = {
        filteredClasses: this.selectedClasses,
      };

      if (this.ClassId) {
        where.ClassId = this.ClassId;
      }

      return classRoomService
        .getAll(where)
        .then((response) => {
          this.items = (response.data || []).sort((a, b) => {
            const nameA = String(a.name || "").toLowerCase();
            const nameB = String(b.name || "").toLowerCase();

            return nameA.localeCompare(nameB);
          });

          this.loading = false;

          return response;
        })
        .catch((error) => {
          console.error("Unable to load classrooms", error);
          this.loading = false;

          this.$swal({
            title: "Classroom List",
            text:
              error &&
              error.data &&
              error.data.message
                ? error.data.message
                : "Unable to load the classroom list.",
            icon: "error",
          });
        });
    },

    editClass(item) {
      this.selectedClassRoom = item;
      this.editDialog = true;
    },

    deleteClass(classroom) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${classroom.name}. This action cannot be reverted.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          classRoomService
            .delete(classroom.id)
            .then((response) => {
              if (response.data) {
                this.$swal(
                  "Deleted!",
                  "Classroom has been deleted.",
                  "success"
                );

                this.getList();
              }
            })
            .catch((error) => {
              this.$swal({
                title: "Delete Classroom",
                text:
                  error &&
                  error.data &&
                  error.data.message
                    ? error.data.message
                    : "Unable to delete this classroom.",
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.classroom-page {
  min-height: 100%;
  padding: 30px 36px 48px;
  background: #f6f8f6;
}

.classroom-page.embedded {
  padding: 0;
  background: white;
}

.classroom-page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-eyebrow {
  margin-bottom: 5px;
  color: #43a047;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.classroom-page-heading h1 {
  margin: 0;
  color: #172033;
  font-size: 29px;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.classroom-page-heading p {
  margin: 6px 0 0;
  color: #728095;
  font-size: 13px;
}

.heading-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.heading-actions .v-btn,
.embedded-actions .v-btn {
  height: 42px;
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.classroom-metrics {
  margin: 0 0 10px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metric-icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eaf6eb;
}

.metric-value {
  color: #253047;
  font-size: 18px;
  font-weight: 750;
  line-height: 1.2;
}

.metric-label {
  margin-top: 2px;
  color: #7b8799;
  font-size: 11px;
}

.classrooms-card {
  overflow: hidden;
  border: 1px solid #e3e9e3;
  border-radius: 11px !important;
}

.classroom-page.embedded .classrooms-card {
  border-radius: 10px !important;
}

.embedded-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 17px;
  border-bottom: 1px solid #edf0ed;
}

.embedded-title {
  color: #28362c;
  font-size: 14px;
  font-weight: 750;
}

.embedded-subtitle {
  margin-top: 2px;
  color: #879388;
  font-size: 10px;
}

.embedded-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.classroom-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 17px 20px;
  border-bottom: 1px solid #edf0ed;
}

.classroom-search {
  max-width: 470px;
}

.classroom-search ::v-deep .v-input__slot {
  border-radius: 9px !important;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.classroom-department-filter {
  min-width: 250px;
}

.classroom-action-btn {
  flex: 0 0 auto;
  width: 40px;
  min-width: 40px !important;
  height: 40px;
}

.classrooms-table ::v-deep th {
  height: 42px;
  color: #718075 !important;
  background: #f8faf8;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.classrooms-table ::v-deep td {
  min-height: 66px;
  padding-top: 9px;
  padding-bottom: 9px;
  color: #425046;
  border-color: #edf0ed !important;
  font-size: 12px;
}

.classroom-row {
  transition: background 0.15s;
}

.classroom-row:hover {
  background: #f7fbf7 !important;
}

.expand-column {
  width: 60px;
  padding-right: 0 !important;
}

.classroom-identity {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 185px;
}

.classroom-avatar {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eaf6eb;
}

.classroom-name-wrap {
  min-width: 0;
}

.classroom-name {
  overflow: hidden;
  color: #253047;
  font-size: 13px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.classroom-subtitle {
  margin-top: 2px;
  color: #879288;
  font-size: 10px;
}

.detail-line {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  min-width: 155px;
}

.detail-line > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.detail-line strong {
  overflow: hidden;
  color: #445047;
  font-size: 11px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-line span {
  margin-top: 2px;
  color: #899489;
  font-size: 9px;
}

.muted-detail strong,
.muted-detail span {
  color: #8a968d;
}

.student-count {
  display: inline-flex;
  min-width: 34px;
  height: 30px;
  padding: 0 9px;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
  border-radius: 8px;
  background: #eaf6eb;
  font-size: 16px;
  font-weight: 800;
}

.teacher-list {
  display: flex;
  min-width: 165px;
  flex-direction: column;
  gap: 5px;
}

.teacher-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.teacher-row span {
  color: #445047;
  font-size: 11px;
  font-weight: 650;
}

.status-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #2e7d32;
  font-size: 10px;
  font-weight: 650;
}

.muted-status {
  color: #8a968d;
}

.warning-status {
  color: #c62828;
}

.chip-list {
  display: flex;
  min-width: 185px;
  flex-wrap: wrap;
  gap: 5px;
}

.expanded-column {
  background: #f7faf7;
}

.classroom-expanded-content {
  padding: 14px;
  border-bottom: 1px solid #e3e9e3;
}

.expanded-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 0 4px 14px;
}

.expanded-heading > div {
  display: flex;
  align-items: center;
  gap: 10px;
}

.expanded-heading > div > div:last-child {
  display: flex;
  flex-direction: column;
}

.expanded-icon {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #eaf6eb;
}

.expanded-heading strong {
  color: #2e3d31;
  font-size: 13px;
}

.expanded-heading span {
  margin-top: 2px;
  color: #899489;
  font-size: 10px;
}

.embedded-student-list {
  overflow: hidden;
  border: 1px solid #e2e8e2;
  border-radius: 10px;
  background: white;
}

.menu-button .v-list-item__icon {
  min-width: 30px;
  margin-right: 4px;
}

.empty-state {
  display: flex;
  min-height: 220px;
  padding: 40px 20px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #526057;
  text-align: center;
}

.empty-state span {
  max-width: 360px;
  color: #929d95;
  font-size: 11px;
}

.empty-state .v-btn {
  margin-top: 8px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
}

.mobile-classroom-list {
  padding: 8px 12px 0;
}

.mobile-classroom-card {
  padding: 16px 3px;
  border-bottom: 1px solid #edf0ed;
}

.mobile-card-top {
  display: flex;
  align-items: center;
}

.mobile-card-identity {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  margin-left: 11px;
}

.mobile-card-identity strong {
  overflow: hidden;
  color: #253047;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-card-identity span {
  margin-top: 2px;
  color: #879288;
  font-size: 11px;
}

.mobile-classroom-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 14px 0 0 51px;
  padding-top: 11px;
  border-top: 1px solid #f0f2f0;
}

.mobile-classroom-details div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.mobile-classroom-details span {
  margin-bottom: 3px;
  color: #919b92;
  font-size: 10px;
  text-transform: uppercase;
}

.mobile-classroom-details strong {
  overflow: hidden;
  color: #445047;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-classroom-details .mobile-student-count {
  color: #2e7d32;
  font-size: 17px;
  font-weight: 800;
}

.mobile-student-toggle {
  margin: 12px 0 0 43px;
  border-radius: 7px;
  text-transform: none;
  letter-spacing: 0;
}

.mobile-expanded-students {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #e3e9e3;
  border-radius: 10px;
  background: #f8faf8;
}

@media (max-width: 1300px) {
  .classrooms-table {
    overflow-x: auto;
  }

  .classrooms-table ::v-deep table {
    min-width: 1180px;
  }
}

@media (max-width: 959px) {
  .classroom-page {
    padding: 22px 14px 36px;
  }

  .classroom-page.embedded {
    padding: 0;
  }

  .classroom-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .classroom-search {
    width: 100%;
    max-width: none;
  }

  .filter-actions {
    flex-wrap: wrap;
  }

  .classroom-department-filter {
    flex: 1;
  }
}

@media (max-width: 599px) {
  .classroom-page {
    padding: 20px 14px 36px;
  }

  .classroom-page.embedded {
    padding: 0;
  }

  .classroom-page-heading {
    align-items: flex-start;
  }

  .classroom-page-heading h1 {
    font-size: 25px;
  }

  .classroom-page-heading p {
    max-width: 220px;
  }

  .heading-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .heading-actions .v-btn {
    min-width: 44px !important;
    padding: 0 11px !important;
  }

  .heading-actions .v-btn:first-child {
    font-size: 0;
  }

  .heading-actions .v-btn:first-child .v-icon {
    margin: 0 !important;
  }

  .classroom-metrics {
    margin-bottom: 8px;
  }

  .classroom-metrics > div {
    padding-top: 6px;
    padding-bottom: 12px;
  }

  .metric-value {
    font-size: 15px;
  }

  .embedded-header {
    align-items: flex-start;
  }

  .embedded-actions {
    flex-direction: column;
  }

  .classroom-filters {
    gap: 12px;
    padding: 14px;
  }

  .filter-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-actions .v-btn {
    align-self: flex-end;
  }
}
</style>
